// Прибитая дата выхода не двигается вместе с задачей. Правило (Варя, 2026-09-10): неделя выхода
// не раньше последней недели работы. Когда план уводит конец задачи за её дату, запись не делается —
// человек решает в окне: перебить дату, деплоить в конце (auto), не деплоить или отменить всё.
import { useEffect, useState } from 'react';
import { Modal } from '@core/ui/Modal';
import { Button } from '@core/ui/Button';
import { addDays, mondayOf, shortDate, sundayOf, type ISODate } from '@core/dates';
import type { Change } from '@core/store/model';
import { barGeometry, type Grid } from './geometry';
import { DeployFlag } from './TaskBar';
import { taskNow, type Task } from './data';

export type DeployChoice = 'pin' | 'auto' | 'none';
export type DeployConflict = { task: Task; newEnd: ISODate };

export const DEPLOY_TOO_EARLY = "Deploy date can't be before the last week of the work";

/** Правило Вари: неделя выхода не раньше последней недели работы. Сравнение по неделям: Release Plan и перенос живут неделями. */
export const deployTooEarly = (deployDate: ISODate, endDate: ISODate): boolean => mondayOf(deployDate) < mondayOf(endDate);

/** Конец задачи уходит за неделю её прибитой даты выхода. */
export const endsAfterDeploy = (t: Pick<Task, 'deploy' | 'deploy_date'>, newEnd: ISODate): boolean =>
  t.deploy === 'date' && !!t.deploy_date && deployTooEarly(t.deploy_date, newEnd);

type DragLike = { task: Task; group: string[]; preview: { kind: string; end_date: ISODate } | null } | null;
type GroupShift = { ids: Set<string>; days: number } | null;

/** Красный контур у бара при протяжке: растягивают за дату выхода или группа уезжает за неё. */
export const dragDanger = (t: Task, isDragged: boolean, drag: DragLike, shifted: boolean, groupShift: GroupShift): boolean =>
  (isDragged && drag?.preview?.kind === 'resize' && endsAfterDeploy(t, drag.preview.end_date))
  || (shifted && !!groupShift && endsAfterDeploy(t, addDays(t.end_date, groupShift.days)));

/** Флажки прибитых дат у тащимой группы: на неделе даты, тянуть можно до него. topOf — верх бара в строке. */
export function DeployFlags({ tasks, drag, grid, height, topOf }: { tasks: Task[]; drag: DragLike; grid: Grid; height: number; topOf: (t: Task) => number }) {
  if (!drag) return null;
  return (
    <>
      {tasks.filter((t) => drag.group.includes(t.id) && t.deploy === 'date' && t.deploy_date).map((t) => {
        const g = barGeometry({ start_date: mondayOf(t.deploy_date!), end_date: sundayOf(t.deploy_date!) }, grid);
        return g ? <DeployFlag key={'flag-' + t.id} left={g.left + g.width} top={topOf(t)} height={height} date={shortDate(t.deploy_date!)} /> : null;
      })}
    </>
  );
}

/** Какие задачи план уводит за их дату выхода: по изменениям end_date против подтверждённой строки. */
export function deployConflictsOf(changes: Change[], now: (id: string) => Task | undefined = taskNow): DeployConflict[] {
  const out: DeployConflict[] = [];
  for (const c of changes) {
    if (!('set' in c) || c.table !== 'task' || typeof c.set['end_date'] !== 'string') continue;
    const t = now(c.id);
    if (t && endsAfterDeploy(t, c.set['end_date'])) out.push({ task: t, newEnd: c.set['end_date'] });
  }
  return out;
}

/** Решение человека — в те же изменения, одной записью: pin — новая дата в конце, auto/none — без даты. */
export function withDeployFix(changes: Change[], conflicts: DeployConflict[], choice: DeployChoice): Change[] {
  const byId = new Map(conflicts.map((c) => [c.task.id, c]));
  return changes.map((c) => {
    if (!('set' in c) || c.table !== 'task' || !byId.has(c.id)) return c;
    const fix = choice === 'pin' ? { deploy: 'date', deploy_date: byId.get(c.id)!.newEnd } : { deploy: choice, deploy_date: null };
    return { ...c, set: { ...c.set, ...fix } };
  });
}

// ---------------------------------------------------------------- окно

/** Обратный случай: чип или карточку релиза бросили на неделю раньше конца работы. */
export type EarlyDeploy = { task: Task; monday: ISODate };
type Question = { kind: 'ends-after'; conflicts: DeployConflict[] } | { kind: 'deploy-early'; early: EarlyDeploy };
type Answer = DeployChoice | 'shift' | null;
type Asker = (q: Question) => Promise<Answer>;
let asker: Asker | null = null;
let asking = false; // второй вопрос поверх открытого окна — отмена: первый промис иначе не резолвится

/** applyPlanned спрашивает здесь; без смонтированного окна (тесты, скрипты) конфликт считается отменой. */
const ask = (q: Question): Promise<Answer> => { if (!asker || asking) return Promise.resolve(null); asking = true; return asker(q).finally(() => { asking = false; }); };
export const askDeploy = (conflicts: DeployConflict[]): Promise<DeployChoice | null> => ask({ kind: 'ends-after', conflicts }) as Promise<DeployChoice | null>;
/** Деплой раньше конца работы: единственный выход — сдвинуть работу раньше; либо отмена. */
export const askEarlyDeploy = (early: EarlyDeploy): Promise<'shift' | null> => ask({ kind: 'deploy-early', early }) as Promise<'shift' | null>;

/** Окно сторожа: живёт на экране, регистрируется как asker. Cancel — ничего не двигается, вся группа на месте. */
export function DeployGuardDialog() {
  const [pending, setPending] = useState<{ q: Question; resolve: (a: Answer) => void } | null>(null);
  useEffect(() => {
    asker = (q) => new Promise((resolve) => setPending({ q, resolve }));
    return () => { asker = null; };
  }, []);
  if (!pending) return null;
  const { q, resolve } = pending;
  const answer = (a: Answer) => { setPending(null); resolve(a); };
  if (q.kind === 'deploy-early') {
    const { task, monday } = q.early;
    const sunday = addDays(monday, 6);
    return (
      <Modal open onClose={() => answer(null)} title="Deploy date" width="max-w-2xl" footer={
        <div className="flex items-center gap-2 flex-wrap" data-deploy-guard>
          <Button onClick={() => answer('shift')}>Move the work to end by {shortDate(sunday)}</Button>
          <span className="ml-auto" />
          <Button variant="ghost" onClick={() => answer(null)}>Cancel</Button>
        </div>
      }>
        <p className="text-base">
          <span className="font-semibold">«{task.title}»</span> ends on {shortDate(task.end_date)}, after the week you dropped it on ({shortDate(monday)} – {shortDate(sunday)}).
          A release can't go out before the work ends. Move the work earlier, or cancel.
        </p>
      </Modal>
    );
  }
  const { conflicts } = q;
  const one = conflicts.length === 1 ? conflicts[0]! : null;
  const pinLabel = one ? `Pin to ${shortDate(one.newEnd)}` : 'Pin to the new ends';
  return (
    <Modal open onClose={() => answer(null)} title="Deploy date" width="max-w-3xl" footer={
      <div className="flex items-center gap-2 flex-wrap" data-deploy-guard>
        <Button onClick={() => answer('pin')}>{pinLabel}</Button>
        <Button variant="secondary" onClick={() => answer('auto')}>Deploy at the end</Button>
        <Button variant="secondary" onClick={() => answer('none')}>Not deploying</Button>
        <span className="ml-auto" />
        <Button variant="ghost" onClick={() => answer(null)}>Cancel</Button>
      </div>
    }>
      {one ? (
        <p className="text-base">
          <span className="font-semibold">«{one.task.title}»</span> would end on {shortDate(one.newEnd)}, after its deploy date ({shortDate(one.task.deploy_date!)}).
          What to do with the deploy date?
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-base">{conflicts.length} tasks would end after their deploy dates. The choice applies to all of them; Cancel leaves every task where it was.</p>
          <ul className="list-disc pl-5 text-sm">
            {conflicts.map((c) => <li key={c.task.id}><span className="font-medium">{c.task.title}</span> — ends {shortDate(c.newEnd)}, deploy {shortDate(c.task.deploy_date!)}</li>)}
          </ul>
        </div>
      )}
    </Modal>
  );
}
